import type { BiteFeedbackEvent, Point } from "@/core/types";
import type { ManeuverType, SkillMoment, BiteBonusResult } from "@/core/scoring/types";
import { FeedbackPriority, type QueuedFeedback } from "./priority";

/**
 * PR13-C (Visual Feedback V2): pure event->text/priority/timing mapping,
 * deliberately framework-free (same split as `priority.ts`) so every rule
 * the spec cares about - RUSH labels following the score combo not speed
 * debt, the CHAOS anticipation->result chain, WORMHOLE REFRESHED never
 * duplicating an activation, legacy SCORE HIT staying renderable - is a
 * plain, directly-testable function. `VisualFeedbackController` is the
 * only caller; it owns turning these into actual Phaser text/graphics.
 */

/** Small bite feedback: ~250-400ms per the spec's motion rules. */
export const NEAR_SNAKE_HOLD_MS = 320;
/** Major headlines: ~650-900ms. */
export const MAJOR_HOLD_MS = 780;
/** CHAOS anticipation stage: ~180-300ms before the result reveals. */
export const CHAOS_ANTICIPATION_HOLD_MS = 240;
/** Skill/Clutch feedback - a touch longer than a bite so a multi-word maneuver name stays readable. */
export const SKILL_HOLD_MS = 700;
/** STARFRUIT's own rare-appearance shimmer notwithstanding, its gate-open headline reads the same as any other major event. */
export const STATUS_ACTIVATION_HOLD_MS = 700;

export type HeadlineKind =
  | "chaos-anticipation"
  | "jackpot"
  | "wormhole-activate"
  | "wormhole-refresh"
  | "shrink"
  | "speed-trial-activate"
  | "score-hit"
  | "speed-trial-success"
  | "starfruit-gate-open"
  | "starfruit-gate-closed"
  | "starfruit-gate-permanent"
  | "gate-opened"
  | "gate-entry"
  | "level-complete";

export interface HeadlineFeedback {
  kind: HeadlineKind;
  text: string;
}

export type SkillKind = "maneuver" | "flow" | "trick-bite" | "clutch-bite" | "wormhole-bite" | "wormhole-clutch";

export interface SkillFeedback {
  kind: SkillKind;
  text: string;
}

function headlineItem(kind: HeadlineKind, text: string, priority: number, holdMs: number): QueuedFeedback<HeadlineFeedback> {
  return { payload: { kind, text }, priority, holdMs };
}

/**
 * Every real fruit/event maps to exactly one headline chain (empty for
 * SAFE/RUSH, which are near-snake-lane-only per the spec). CHAOS always
 * produces a two-stage chain: `CHAOS!` first, then the resolved outcome -
 * `wormholeAlreadyActive`/`isWormholeRefresh` decides only the WORMHOLE
 * outcome's own text (`WORMHOLE!` vs `WORMHOLE REFRESHED!`), never a
 * second, duplicate activation headline.
 */
export function mapBiteEventToHeadlineChain(event: BiteFeedbackEvent, ctx: { isWormholeRefresh: boolean }): QueuedFeedback<HeadlineFeedback>[] {
  switch (event.kind) {
    case "safe":
    case "rush":
      return [];
    case "starfruit":
      return [headlineItem("starfruit-gate-open", "STAR GATE OPEN!", FeedbackPriority.majorEvent, MAJOR_HOLD_MS)];
    case "speed-trial-success":
      return [
        headlineItem(
          "speed-trial-success",
          `SURVIVED! +${event.rewardGrowth} LENGTH · +${event.rewardScore} SCORE`,
          FeedbackPriority.statusActivation,
          MAJOR_HOLD_MS,
        ),
      ];
    case "chaos": {
      const anticipation = headlineItem("chaos-anticipation", "CHAOS!", FeedbackPriority.majorEvent, CHAOS_ANTICIPATION_HOLD_MS);
      return [anticipation, chaosResultHeadline(event, ctx)];
    }
  }
}

function chaosResultHeadline(event: Extract<BiteFeedbackEvent, { kind: "chaos" }>, ctx: { isWormholeRefresh: boolean }): QueuedFeedback<HeadlineFeedback> {
  switch (event.outcome) {
    case "jackpot":
      return headlineItem("jackpot", `JACKPOT! +${event.growth} LENGTH · +${event.score} SCORE`, FeedbackPriority.majorEvent, MAJOR_HOLD_MS);
    case "wormhole":
      return ctx.isWormholeRefresh
        ? headlineItem("wormhole-refresh", "WORMHOLE REFRESHED!", FeedbackPriority.majorEvent, MAJOR_HOLD_MS)
        : headlineItem("wormhole-activate", "WORMHOLE!", FeedbackPriority.majorEvent, MAJOR_HOLD_MS);
    case "speed-trial":
      return headlineItem("speed-trial-activate", "SPEED TRIAL!", FeedbackPriority.majorEvent, MAJOR_HOLD_MS);
    case "shrink":
      return headlineItem(
        "shrink",
        event.segmentsRemoved === 0
          ? `SHRINK! MINIMUM LENGTH · -${event.scoreLoss} SCORE`
          : `SHRINK! -${event.segmentsRemoved} LENGTH · -${event.scoreLoss} SCORE`,
        FeedbackPriority.majorEvent,
        MAJOR_HOLD_MS,
      );
    case "score-hit":
      // Legacy Daily V1-V4 outcome - unreachable from the current
      // Campaign/Daily V5 pool, but its visual must stay renderable for a
      // frozen Daily fixture that can still emit it.
      return headlineItem(
        "score-hit",
        event.wasFlat ? `SCORE HIT! ${event.previousScore} -> ${event.newScore}` : `SCORE HIT! -${event.percent}%`,
        FeedbackPriority.majorEvent,
        MAJOR_HOLD_MS,
      );
  }
}

/**
 * "Tactile Impact Polish" follow-up: the signed Bite Score delta this
 * event actually applied - read straight off the engine's own numbers on
 * `event` (never recomputed/guessed), used for both the near-bite delta
 * label and the HUD score bump. `0` for an event that never touches Bite
 * Score at all (WORMHOLE/SPEED TRIAL activation, STARFRUIT - which
 * "does not add normal score or length" by design).
 */
export function biteScoreDelta(event: BiteFeedbackEvent): number {
  switch (event.kind) {
    case "safe":
    case "rush":
      return event.score;
    case "starfruit":
      return 0;
    case "speed-trial-success":
      return event.rewardScore;
    case "chaos":
      switch (event.outcome) {
        case "jackpot":
          return event.score;
        case "shrink":
          return -event.scoreLoss;
        case "score-hit":
          return event.delta;
        case "wormhole":
        case "speed-trial":
          return 0;
      }
  }
}

export type ImpactLabelKind = "safe" | "rush" | "gain" | "loss" | "shrink";

export interface ImpactLabelPayload {
  kind: ImpactLabelKind;
  text: string;
}

/**
 * The near-bite-cell delta label lane's payload for any event that
 * actually changed Bite Score - SAFE/RUSH always (with RUSH now also
 * carrying its own real score delta, not just the combo label), and any
 * other event (JACKPOT/SHRINK/SCORE HIT/a completed SPEED TRIAL) only
 * when `biteScoreDelta()` is non-zero. `null` for an event with no score
 * change to show (WORMHOLE/SPEED TRIAL activation, STARFRUIT).
 *
 * QA follow-up (visual correctness): SHRINK's own negative delta gets the
 * dedicated `"shrink"` kind, never the generic `"loss"` kind legacy SCORE
 * HIT still uses - SHRINK is a CHAOS result and must render with no red
 * anywhere, including this label, while SCORE HIT's own red stays
 * unchanged. The numeric value/sign is identical either way (`-N`).
 */
export function mapBiteEventToNearSnake(event: BiteFeedbackEvent): QueuedFeedback<ImpactLabelPayload> | null {
  if (event.kind === "safe") {
    return { payload: { kind: "safe", text: `+${event.score}` }, priority: FeedbackPriority.safe, holdMs: NEAR_SNAKE_HOLD_MS };
  }
  if (event.kind === "rush") {
    return { payload: { kind: "rush", text: `${rushLabel(event.stack)} +${event.score}` }, priority: FeedbackPriority.rush, holdMs: NEAR_SNAKE_HOLD_MS };
  }
  const delta = biteScoreDelta(event);
  if (delta === 0) return null;
  const isShrink = event.kind === "chaos" && event.outcome === "shrink";
  const kind: ImpactLabelKind = isShrink ? "shrink" : delta > 0 ? "gain" : "loss";
  return {
    payload: { kind, text: delta > 0 ? `+${delta}` : `${delta}` },
    priority: FeedbackPriority.outcomeDetail,
    holdMs: NEAR_SNAKE_HOLD_MS + 100,
  };
}

/**
 * RUSH's own escalating visual-intensity scale - strictly monotonic 1..5
 * with the score-combo stack (never speed debt), used only to scale ring
 * size/afterimage count/pulse brightness. Never touches movement speed or
 * combo rules - those stay exactly as `SpeedEngine`/`LevelGame` compute
 * them.
 */
export function rushIntensity(stack: number): number {
  return Math.max(1, Math.min(5, stack));
}

/**
 * The coarse, cross-kind intensity tier the shared bite-impact sequence
 * (ring size, HUD bump strength) scales by - a strictly increasing scale
 * matching the locked hierarchy: SAFE < RUSH x1 < ... < RUSH x5 <
 * a normal CHAOS outcome < JACKPOT/STARFRUIT < level completion. Every
 * RUSH stack level is its own distinct value (`1 + rushIntensity(stack)`)
 * so the x1..x5 escalation stays strictly monotonic even though it all
 * sits below a normal CHAOS outcome's own tier.
 */
export function eventIntensityTier(event: BiteFeedbackEvent): number {
  switch (event.kind) {
    case "safe":
      return 1;
    case "rush":
      return 1 + rushIntensity(event.stack); // 2..6
    case "starfruit":
      return 8;
    case "speed-trial-success":
      return 7;
    case "chaos":
      switch (event.outcome) {
        case "jackpot":
          return 8;
        case "shrink":
        case "speed-trial":
        case "wormhole":
        case "score-hit":
          return 7;
      }
  }
}

/** Level completion is the single strongest tier in the hierarchy - strictly above JACKPOT/STARFRUIT's tier 8. */
export const LEVEL_COMPLETE_INTENSITY_TIER = 9;

/**
 * PR31 follow-up (visual-language pass, feedback intensity hierarchy).
 * The three named bands the spec asks for - MICRO (routine: a normal SAFE
 * bite, an ordinary score increase), MEDIUM (meaningful: a RUSH increase,
 * SHRINK, WORMHOLE, SPEED TRIAL), BIG (rare/high-value: JACKPOT, a gate
 * opening, level completion) - mapped straight onto the intensity scale
 * `eventIntensityTier()`/`LEVEL_COMPLETE_INTENSITY_TIER` already define,
 * not a second, parallel numbering:
 *
 *   micro  = tier 1                (SAFE only)
 *   medium = tier 2..7             (every RUSH stack, SHRINK, WORMHOLE,
 *                                    SPEED TRIAL activation/success,
 *                                    legacy SCORE HIT)
 *   big    = tier 8+                (JACKPOT, STARFRUIT, level completion)
 *
 * This is deliberately just a thin, named lookup over numbers that already
 * exist - `VisualFeedbackController` already scales its shared pop/ring/
 * head-pulse and `pulseScoreHud()`'s own scale continuously BY `tier`
 * (so MICRO is already smaller/briefer than BIG by construction), and
 * already reserves its stacked extra primitives (`playBurstRing()`,
 * `playBodyPulse()`, `playFrameFlash()`) for BIG-band outcomes only
 * (JACKPOT gets all three; WORMHOLE and a completed SPEED TRIAL, both
 * MEDIUM's own top tier, get at most one). Every gate/status headline
 * that isn't a `BiteFeedbackEvent` at all (gate opened, level completion)
 * is already the highest `FeedbackPriority` tier (`majorEvent`/`runEnd`)
 * in `priority.ts`, which is this same "BIG" identity expressed through
 * the queue's own priority number instead of `eventIntensityTier`'s.
 * Nothing here changes how any effect actually scales - see this
 * module's and `VisualFeedbackController`'s own tests/doc comments for
 * that; this only names the three bands so the hierarchy the spec asks
 * for is a real, testable artifact instead of an implicit pattern.
 */
export type IntensityBand = "micro" | "medium" | "big";

export function intensityBand(tier: number): IntensityBand {
  if (tier <= 1) return "micro";
  if (tier <= 7) return "medium";
  return "big";
}

/**
 * SHRINK's ghost-segment positions - the actual tail cells the engine
 * just removed, read straight from the pre-move snake body (the last
 * `segmentsRemoved` cells, which is exactly what shrinking off the tail
 * means) - never invented removal geometry. `[]` when nothing was
 * actually removed (the snake was already at its minimum length), so the
 * caller can fall back to a smaller failed-contract pulse instead of
 * pretending segments disappeared.
 */
export function shrinkGhostSegments(previousSnake: readonly Point[], segmentsRemoved: number): readonly Point[] {
  if (segmentsRemoved <= 0) return [];
  return previousSnake.slice(Math.max(0, previousSnake.length - segmentsRemoved));
}

/** Clamps a screen-space label position inside safe visible bounds - desktop and mobile alike - so a near-gate/near-bite label can never render off-screen or outside the board's own margins. */
export function clampToBounds(
  point: Point,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
): Point {
  return {
    x: Math.min(Math.max(point.x, bounds.minX), bounds.maxX),
    y: Math.min(Math.max(point.y, bounds.minY), bounds.maxY),
  };
}

/**
 * RUSH's escalating copy - driven by the actual score-combo stack (capped
 * 1-5, already reset by SAFE/CHAOS/STARFRUIT per existing rules), never
 * `cumulativeYellowCount` (permanent RUSH speed debt, never reset). See
 * `LevelGameSnapshot.rushStack`'s own doc for why these are deliberately
 * different numbers.
 */
export function rushLabel(stack: number): string {
  const capped = Math.max(1, Math.min(5, stack));
  return capped === 1 ? "RUSH!" : `RUSH ×${capped}`;
}

/** Exported so the `?qa=scoring` debug overlay (`GameScene.updateQaScoringOverlay()`, unrelated to this PR) can keep using the exact same maneuver labels instead of maintaining a second copy. */
export const MANEUVER_LABEL: Record<ManeuverType, string> = {
  perfect_fold: "PERFECT FOLD",
  last_second_turn: "LAST-SECOND TURN",
  tail_graze: "TAIL GRAZE",
  thread_the_needle: "THREAD THE NEEDLE",
};

/** A credited skill moment's Skill-lane feedback, plus its own FLOW line when the multiplier is above x1 - both share the Skill lane's priority tier, FLOW simply following right behind via `enqueueChain`. */
export function mapSkillMomentToSkillFeedback(moment: SkillMoment): QueuedFeedback<SkillFeedback>[] {
  const names = moment.maneuvers.map((m) => MANEUVER_LABEL[m.type]);
  const label = moment.doubleDanger ? `${names.join(" + ")} — DOUBLE DANGER!` : names.join(" + ");
  const items: QueuedFeedback<SkillFeedback>[] = [
    { payload: { kind: "maneuver", text: `${label} +${moment.awardedPoints}` }, priority: FeedbackPriority.skill, holdMs: SKILL_HOLD_MS },
  ];
  if (moment.flowMultiplier > 1) {
    items.push({
      payload: { kind: "flow", text: `FLOW x${moment.flowMultiplier.toFixed(2)}` },
      priority: FeedbackPriority.skill,
      holdMs: SKILL_HOLD_MS,
    });
  }
  // PR26 LAST-SECOND TURN V4 requirement #8: a compact secondary line
  // naming the base curve value and the persistent-speed multiplier that
  // fed into the `+points` line above - informational only (no "="), so
  // it never reads as a full equation that would go wrong the moment
  // FLOW/DOUBLE DANGER also touched the final number.
  const lastSecondTurn = moment.maneuvers.find((m) => m.type === "last_second_turn");
  if (lastSecondTurn && moment.lastSecondTurnSpeedMultiplier > 1) {
    items.push({
      payload: { kind: "flow", text: `BASE ${lastSecondTurn.points} · SPEED ×${moment.lastSecondTurnSpeedMultiplier.toFixed(2)}` },
      priority: FeedbackPriority.skill,
      holdMs: SKILL_HOLD_MS,
    });
  }
  return items;
}

/** TRICK/CLUTCH BITE (including its WORMHOLE-sourced variant) - same Skill lane, same priority tier as a geometric maneuver, so the two families never fight over the lane independently of each other. */
export function mapBiteBonusToSkillFeedback(bonus: BiteBonusResult): QueuedFeedback<SkillFeedback> {
  const kind: SkillKind = bonus.source === "wormhole" ? (bonus.kind === "clutch" ? "wormhole-clutch" : "wormhole-bite") : bonus.kind === "clutch" ? "clutch-bite" : "trick-bite";
  const text =
    bonus.source === "wormhole"
      ? bonus.kind === "clutch"
        ? `WORMHOLE CLUTCH! +${bonus.points}`
        : `WORMHOLE BITE! +${bonus.points}`
      : bonus.kind === "clutch"
        ? `CLUTCH BITE +${bonus.points}`
        : `TRICK BITE +${bonus.points}`;
  return { payload: { kind, text }, priority: FeedbackPriority.skill, holdMs: SKILL_HOLD_MS };
}

/** The gate just became enterable (real target length reached, or STARFRUIT opened it) - not yet the run ending, so it shares the same tier as a major CHAOS result/STARFRUIT gate open, not `runEnd`. */
export function gateOpenedHeadline(): QueuedFeedback<HeadlineFeedback> {
  return headlineItem("gate-opened", "EXIT OPEN!", FeedbackPriority.majorEvent, MAJOR_HOLD_MS);
}

/** The head just walked into the already-open gate - the run's outcome is now locked in (the automatic exit walk always ends in completion), so this shares `death`/`level-complete`'s top `runEnd` tier. */
export function gateEntryHeadline(): QueuedFeedback<HeadlineFeedback> {
  return headlineItem("gate-entry", "ENTERING GATE!", FeedbackPriority.runEnd, MAJOR_HOLD_MS);
}

export function levelCompleteHeadline(): QueuedFeedback<HeadlineFeedback> {
  return headlineItem("level-complete", "LEVEL COMPLETE!", FeedbackPriority.runEnd, MAJOR_HOLD_MS);
}

// ---------------------------------------------------------------------
// WORMHOLE / STARFRUIT gate transitions - derived purely from two
// consecutive authoritative snapshots (never a UI-owned timer), so the
// visual can never drift from what the engine actually did.
// ---------------------------------------------------------------------

export type WormholeTransition = "activated" | "refreshed" | "ticking" | "ended" | "none";

/**
 * `before`/`after` are each just the one field this needs - narrowed so a
 * test fixture never has to fake an entire `LevelGameSnapshot`. A refresh
 * is detected purely from the remaining time going UP (a fresh WORMHOLE
 * always resets to the full 10s, strictly more than whatever a ticking
 * countdown had left) - the same authoritative-time rule the timer badge
 * itself uses, never a second, independent "was this actually two
 * separate CHAOS rolls" flag.
 */
export function classifyWormholeTransition(beforeRemainingMs: number | null, afterRemainingMs: number | null): WormholeTransition {
  if (beforeRemainingMs === null && afterRemainingMs !== null) return "activated";
  if (beforeRemainingMs !== null && afterRemainingMs === null) return "ended";
  if (beforeRemainingMs !== null && afterRemainingMs !== null) {
    return afterRemainingMs > beforeRemainingMs ? "refreshed" : "ticking";
  }
  return "none";
}

export type StarfruitGateTransition = "opened" | "expired" | "promoted-permanent" | "consumed-by-entry" | "ticking" | "none";

export interface StarfruitGateSnapshotSlice {
  starfruitGateRemainingMs: number | null;
  isGateOpen: boolean;
  phase: "playing" | "exiting" | "game-over" | "level-complete";
}

/**
 * `timerEventWasExpiry` is true exactly when `advanceRealTime()` returned
 * `{ kind: "starfruit-gate-expired" }` this frame - the direct engine
 * signal for "the temporary window ran out on its own", as opposed to
 * every other way `starfruitGateRemainingMs` can go from non-null to null
 * (gate entry, or the real target length being reached while it was
 * open - promoted to permanent, never "closed").
 */
export function classifyStarfruitGateTransition(
  before: StarfruitGateSnapshotSlice,
  after: StarfruitGateSnapshotSlice,
  timerEventWasExpiry: boolean,
): StarfruitGateTransition {
  const beforeMs = before.starfruitGateRemainingMs;
  const afterMs = after.starfruitGateRemainingMs;
  if (beforeMs === null && afterMs !== null) return "opened";
  if (beforeMs !== null && afterMs === null) {
    if (timerEventWasExpiry) return "expired";
    if (after.phase === "exiting" || after.phase === "level-complete") return "consumed-by-entry";
    if (after.isGateOpen) return "promoted-permanent";
    return "expired";
  }
  if (beforeMs !== null && afterMs !== null) return "ticking";
  return "none";
}

export function starfruitGateExpiredHeadline(): QueuedFeedback<HeadlineFeedback> {
  return headlineItem("starfruit-gate-closed", "GATE CLOSED!", FeedbackPriority.majorEvent, MAJOR_HOLD_MS);
}
